import { Dialog, DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { HeroEntity } from '@generated/gql';
import { getTokenType } from '@helpers/data-helper';
import { Formatter } from '@helpers/formatter';
import { TranslateModule } from '@ngx-translate/core';
import { DestroyService } from '@services/destroy.service';
import { SubgraphService } from '@services/graph/subgraph.service';
import { HeroControllerService } from '@services/onchain/hero-controller.service';
import { TokenService } from '@services/onchain/token.service';
import { UniswapV3Service } from '@services/onchain/uniswap-v3.service';
import { ProviderService } from '@services/provider.service';
import { ButtonClickDirective } from '@shared/button-click/button-click.directive';
import { DialogTitleComponent } from '@shared/components/dialog-title/dialog-title.component';
import { LoadingSmallComponent } from '@shared/components/loading-small/loading-small.component';
import { GET_CORE_ADDRESSES } from '@shared/constants/addresses/addresses.constant';
import { NUMBERS } from '@shared/constants/numbers.constant';
import { formatUnits } from 'ethers';
import { map, switchMap, first, finalize, BehaviorSubject, of, takeUntil, forkJoin } from 'rxjs';

export interface UnlockHeroDialogParams {
  hero: HeroEntity;
}

@Component({
  standalone: true,
  imports: [DialogTitleComponent, TranslateModule, AsyncPipe, LoadingSmallComponent, ButtonClickDirective],
  templateUrl: './unlock-hero-dialog.component.html',
  styleUrl: './unlock-hero-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'app-window-responsive-background g-flex-column',
  },
})
export class UnlockHeroDialogComponent implements OnInit {
  private readonly dialogRef = inject(DialogRef);
  private readonly subgraphService = inject(SubgraphService);
  private readonly tokenService = inject(TokenService);
  private readonly providerService = inject(ProviderService);
  private readonly destroy$ = inject(DestroyService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly uniswapV3Service = inject(UniswapV3Service);
  private readonly heroControllerService = inject(HeroControllerService);
  private readonly dialogData: UnlockHeroDialogParams = inject(DIALOG_DATA);
  private readonly router = inject(Router);
  private readonly dialog = inject(Dialog);

  private readonly chainId$ = new BehaviorSubject<number>(1);
  private readonly account$ = new BehaviorSubject<string>('');
  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);

  protected readonly isAllowance$ = new BehaviorSubject<boolean>(false);
  protected readonly payAmount$ = of(+this.dialogData.hero.meta.feeToken.amount);
  protected readonly payAmountUsd$ = this.uniswapV3Service.nativePrice$(Number(environment['CHAIN_ID'])).pipe(
    switchMap(tokenUsdPrice =>
      this.payAmount$.pipe(
        map(payAmount => +payAmount * tokenUsdPrice),
        map(price => Formatter.formatCurrency(price)),
      ),
    ),
  );
  protected heroTokenType = '';
  protected hasBalance = false;

  public ngOnInit(): void {
    this.providerService.subscribeOnAccountAndNetwork(
      this.destroy$,
      this.changeDetectorRef,
      account => {
        this.account$.next(account);
        this.init();
      },
      chainId => {
        this.chainId$.next(chainId);
        this.init();
      },
    );
  }

  init(): void {
    this.loadBalances();
  }

  private loadBalances() {
    if (this.account$.value && this.chainId$.value) {
      const account = this.account$.value;
      const chainId = this.chainId$.value;
      this.heroTokenType = getTokenType(this.dialogData.hero.meta.feeToken.token.id, chainId);
      this.isLoading$.next(true);
      forkJoin({
        allowance: this.tokenService.allowance$(
          this.dialogData.hero.meta.feeToken.token.id,
          account,
          GET_CORE_ADDRESSES(chainId).controller,
        ),
        payAmount: this.payAmount$,
        balance: this.tokenService.balanceOf$(this.dialogData.hero.meta.feeToken.token.id, account),
      })
        .pipe(takeUntil(this.destroy$))
        .subscribe(({ allowance, payAmount, balance }) => {
          this.isAllowance$.next(+formatUnits(allowance) >= payAmount);
          this.hasBalance = +formatUnits(balance) >= payAmount;
          this.isLoading$.next(false);
          this.changeDetectorRef.detectChanges();
        });
    }
  }

  protected approve() {
    this.isLoading$.next(true);
    this.tokenService
      .approve$(
        this.account$.value,
        this.dialogData.hero.meta.feeToken.token.id,
        GET_CORE_ADDRESSES(this.chainId$.value).controller,
        BigInt(NUMBERS.MAX_UINT),
      )
      .pipe(
        finalize(() => {
          this.isLoading$.next(false);
          this.changeDetectorRef.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.init();
      });
  }

  protected close(): void {
    this.dialogRef.close(false);
  }

  protected unlock(): void {
    if (this.isLoading$.value) {
      return;
    }
    this.isLoading$.next(true);

    this.chainId$
      .pipe(
        first(),
        switchMap(chainId =>
          this.heroControllerService.upgradeSandboxHero$(
            chainId,
            this.dialogData.hero.meta.id,
            this.dialogData.hero.heroId,
          ),
        ),
        finalize(() => this.isLoading$.next(false)),
      )
      .subscribe(() => {
        this.dialogRef.close(true);
      });
  }
}
